import React from 'react';
import { motion } from 'framer-motion';
import { Section } from './index';

/**
 * About section component explaining BigCash Coffee Token history and features
 */
const About = () => {
  const features = [
    {
      title: 'Blockchain-Based Token',
      description: 'Built on Ethereum blockchain for transparency, security, and decentralized ownership.',
      icon: '🔗'
    },
    {
      title: 'Rewards Coffee Lovers',
      description: 'Earn BigCash tokens through coffee purchases and community participation.',
      icon: '☕'
    },
    {
      title: 'Community-Driven',
      description: 'Governed by the community with voting rights and collaborative decision-making.',
      icon: '🤝'
    }
  ];
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };
  
  return (
    <Section id="about" background="cream" className="relative">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-10 left-10 text-6xl">☕</div>
        <div className="absolute top-20 right-20 text-4xl">🫘</div>
        <div className="absolute bottom-20 left-20 text-5xl">☕</div>
        <div className="absolute bottom-10 right-10 text-3xl">🫘</div>
      </div>
      
      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
        className="relative z-10"
      >
        {/* Section Header */}
        <motion.div variants={itemVariants} className="text-center mb-20">
          <h2 className="text-5xl md:text-7xl font-black font-display mb-8 leading-tight">
            <span className="bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 bg-clip-text text-transparent drop-shadow-lg">
              About BigCash
            </span>
          </h2>
          <div className="w-32 h-2 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 mx-auto mb-6 rounded-full shadow-lg"></div>
          <p className="text-xl md:text-2xl text-coffee-600 font-medium max-w-3xl mx-auto">
            Discover the story behind Malaysia's revolutionary coffee token
          </p>
        </motion.div>
        
        {/* Token History */}
        <motion.div variants={itemVariants} className="mb-20">
          <div className="relative max-w-5xl mx-auto">
            <div className="glassmorphism rounded-3xl p-8 md:p-12 shadow-3xl border border-amber-200/30">
              <div className="text-center">
                <h3 className="text-3xl md:text-4xl font-bold mb-8">
                  <span className="bg-gradient-to-r from-coffee-800 via-amber-700 to-orange-700 bg-clip-text text-transparent">
                    From E-commerce to Coffee Culture
                  </span>
                </h3>
                
                <div className="grid md:grid-cols-2 gap-8 text-left">
                  <div className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <span className="text-3xl">🚀</span>
                      <div>
                        <h4 className="text-xl font-bold text-coffee-800 mb-2">The Beginning</h4>
                        <p className="text-coffee-700 leading-relaxed">
                          BigCash Token began its journey in the e-commerce space, but evolved to embrace 
                          Malaysia's vibrant coffee culture.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-4">
                      <span className="text-3xl">☕</span>
                      <div>
                        <h4 className="text-xl font-bold text-coffee-800 mb-2">Coffee Connection</h4>
                        <p className="text-coffee-700 leading-relaxed">
                          We recognized that coffee brings people together, creates communities, 
                          and deserves a token that celebrates this universal passion.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <span className="text-3xl">🇲🇾</span>
                      <div>
                        <h4 className="text-xl font-bold text-coffee-800 mb-2">Malaysia's First</h4>
                        <p className="text-coffee-700 leading-relaxed">
                          Today, BigCash stands as Malaysia's first coffee token, bridging traditional 
                          coffee culture and modern blockchain technology.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-4">
                      <span className="text-3xl">🌱</span>
                      <div>
                        <h4 className="text-xl font-bold text-coffee-800 mb-2">Our Mission</h4>
                        <p className="text-coffee-700 leading-relaxed">
                          Building a sustainable ecosystem that rewards coffee enthusiasts 
                          across Malaysia and beyond.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* Features Grid */}
        <motion.div variants={itemVariants}>
          <div className="text-center mb-16">
            <h3 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 bg-clip-text text-transparent">
                What Makes BigCash Special
              </span>
            </h3>
            <p className="text-xl text-coffee-600 max-w-2xl mx-auto">
              Discover the unique features that set our coffee token apart
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.05, y: -10 }}
                className="group"
              >
                <div className="glassmorphism rounded-2xl p-8 text-center h-full shadow-2xl border border-amber-200/30 group-hover:border-amber-300/50 transition-all duration-300">
                  <div className="text-5xl mb-6 animate-bounce-slow">{feature.icon}</div>
                  <h4 className="text-2xl font-bold mb-4">
                    <span className="bg-gradient-to-r from-coffee-800 to-amber-700 bg-clip-text text-transparent">
                      {feature.title}
                    </span>
                  </h4>
                  <p className="text-coffee-600 leading-relaxed text-lg">
                    {feature.description}
                  </p>
                  <div className="mt-6 w-16 h-1 bg-gradient-to-r from-amber-500 to-orange-500 mx-auto rounded-full"></div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
        
        {/* Call to Action */}
        <motion.div variants={itemVariants} className="text-center mt-20">
          <div className="relative max-w-4xl mx-auto">
            <div className="glassmorphism rounded-3xl p-8 md:p-12 shadow-3xl border border-amber-200/30 bg-gradient-to-br from-amber-50/50 to-orange-50/50">
              <h3 className="text-3xl md:text-4xl font-bold mb-6">
                <span className="bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 bg-clip-text text-transparent">
                  Join the Coffee Revolution
                </span>
              </h3>
              <p className="text-xl text-coffee-600 mb-8 max-w-3xl mx-auto leading-relaxed">
                Be part of Malaysia's first coffee token community and help shape the future 
                of coffee culture through blockchain technology.
              </p>
              
              <div className="flex justify-center items-center space-x-6 mb-6">
                <div className="flex items-center space-x-2">
                  <span className="text-3xl animate-bounce-slow">☕</span>
                  <span className="text-lg font-semibold text-coffee-700">Premium Coffee</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-3xl animate-pulse-slow">🚀</span>
                  <span className="text-lg font-semibold text-coffee-700">Blockchain Tech</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-3xl animate-float">🇲🇾</span>
                  <span className="text-lg font-semibold text-coffee-700">Made in Malaysia</span>
                </div>
              </div>
              
              <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-orange-500 mx-auto rounded-full"></div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </Section>
  );
};

export default About;